/**
 * useDespesaForm — a lógica de lançar/editar despesa, uma vez só.
 *
 * POR QUE ISTO EXISTE. O lançamento tem DUAS PORTAS DE ENTRADA, com intenções
 * diferentes:
 *
 *   1. Em campo, do celular (`/despesas/nova`) — o instalador ou a sócia com a
 *      nota na mão, pelo atalho do rodapé do app instalado. Não passa por lista
 *      nenhuma: abriu, lançou, fechou.
 *   2. No sistema, do desktop (`/despesas/lancar` e `/despesas/:id`) — a
 *      Patrícia lançando a notinha que o instalador entregou, ou ajustando uma
 *      despesa já lançada. Trabalho sentado, tudo à vista.
 *
 * São duas TELAS, porque um arranjo bom para uma é ruim para a outra. Mas é uma
 * REGRA só: validação, conversão de valor, upload de comprovante, herança de
 * loja pela viagem, o que fazer ao salvar. Se essa regra morasse duas vezes, as
 * duas telas divergiriam — foi exatamente assim que o formulário público ganhou
 * busca de OS por digitação e o interno ficou para trás.
 *
 * Então: a regra mora aqui, e cada vista só desenha.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { hojeISO } from '@/lib/data';
import { doBanco, paraBanco, paraConta } from '@/lib/numero';
import { comprimirImagem } from '@/lib/imagem';
import { useAuth } from '@/auth/auth-context';
import type {
  Categoria,
  ContextoCategoria,
  DespesaSaida,
  FormaPgtoOpcao,
  Loja,
  OSOpcao,
  UploadComprovante,
  ViagemOpcao,
} from './shared';
import { contextoDoPerfil } from './shared';

export interface FormState {
  data: string;
  /** Valor Rápido: string canônica com ponto decimal ('' | '80.66'), não float. */
  valor: string;
  historico: string;
  descricao: string;
  categoria_id: string;
  forma_pgto: string;
  loja_cartao_id: string; // cartão de qual loja (só p/ 'Cartão da loja')
  loja_id: string;
  viagem_id: string;
  os_id: string;
  comprovante: string | null; // caminho relativo (ex.: 'despesas/uuid.jpg')
}

const FORM_VAZIO: FormState = {
  data: hojeISO(),
  valor: '',
  historico: '',
  descricao: '',
  categoria_id: '',
  forma_pgto: '',
  loja_cartao_id: '',
  loja_id: '',
  viagem_id: '',
  os_id: '',
  comprovante: null,
};

export interface DespesaForm {
  form: FormState;
  setCampo: <K extends keyof FormState>(campo: K, valor: FormState[K]) => void;
  /** Troca a viagem e herda a loja dela — nunca mexer em `viagem_id` na mão. */
  escolherViagem: (viagemId: string) => void;
  /** "Sem viagem" marcado. No celular, ou isto ou uma viagem — dele, 26/09. */
  semViagem: boolean;
  marcarSemViagem: (v: boolean) => void;
  /** Troca a forma de pagamento (limpa "cartão de qual loja" quando não se aplica). */
  escolherForma: (codigo: string) => void;

  formas: FormaPgtoOpcao[];
  formaEscolhida?: FormaPgtoOpcao;
  /** A forma escolhida pede "cartão de qual loja?" (Cartão da loja). */
  pedeLoja: boolean;
  lojas: Loja[];
  categorias: Categoria[];
  viagens: ViagemOpcao[];
  osLista: OSOpcao[];
  categoriaEscolhida?: Categoria;
  rotuloLoja: (l: Loja) => string;

  editando: boolean;
  origem: string | null;
  lancadoPor: string | null;
  /** Já foi para o Tiny (ou está com cadeado): valor, data, loja, categoria e
   *  pagamento travam — dele, 25/09. O backend recusa igual (409). */
  noTiny: boolean;
  setNoTiny: (v: boolean) => void;
  /** Quando foi arquivada (fora da lista de trabalho), ou null. */
  arquivadaEm: string | null;
  setArquivadaEm: (v: string | null) => void;
  contexto: ContextoCategoria;
  ehGestao: boolean;
  destinoAoSair: string;

  carregando: boolean;
  salvando: boolean;
  enviandoFoto: boolean;
  fileRef: React.RefObject<HTMLInputElement | null>;
  onArquivo: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removerComprovante: () => void;
  salvar: (e: React.FormEvent) => void;
  sair: () => void;
}

export function useDespesaForm(
  id: string | undefined,
  opts: { desktop?: boolean } = {},
): DespesaForm {
  const navigate = useNavigate();
  const { usuario, pode } = useAuth();
  // Desktop (form-completo) mostra TODAS as formas; celular (form-campo) filtra.
  const desktop = !!opts.desktop;

  // Só dígito abre modo edição. A lista de exceções ('nova', 'lancar') protegia
  // apenas os nomes conhecidos: QUALQUER outro segmento virava `Number(...)` =
  // NaN e mandava `PUT /api/despesas/NaN`. Perguntar "é um id?" fecha a porta
  // para o próximo nome que alguém inventar, em vez de listar os que já existem.
  const editando = !!id && /^\d+$/.test(id);
  const despesaId = editando ? Number(id) : null;

  /**
   * Para onde se volta ao sair.
   *
   * A lista de `/despesas` mostra o que a equipe inteira gastou, e pede
   * `despesas.lancamento.ver` (o backend recusa com 403 os demais — "por
   * enquanto, os funcionários vão apenas lançar despesas", 09/08/2026). Quem
   * não a tem volta para as PRÓPRIAS despesas: ver o que acabou de lançar é o
   * que evita lançar de novo por dúvida.
   *
   * Era `perfil === 'gestor'` até 26/08/2026 — a etiqueta do perfil no lugar
   * da permissão mandava a Patrícia (financeiro) para uma tela que o backend
   * lhe recusaria.
   */
  const ehGestao = pode('despesas.lancamento.ver');
  // NO CELULAR, TODO MUNDO VOLTA PARA O INÍCIO — inclusive a gestão. Decisão
  // dele, 27/08/2026, corrigindo a primeira versão desta linha (que decidia
  // pelo PERFIL): *"vamos mandar todos para a tela inicial... nesse caso eu tô
  // falando do acesso pelo celular. Você vai lançar uma despesa pelo celular,
  // aquele formulário volta para o menu inicial"*.
  //
  // O critério é o APARELHO, não quem é a pessoa: no celular se lança UMA
  // despesa e acabou ali — a lista é uma tela que ninguém foi ao celular para
  // usar. No desktop a gestão volta à lista, porque lá o fluxo é lançar,
  // conferir e lançar de novo, e foi da lista que ela saiu.
  //
  // Vale para salvar E para cancelar: os dois usam este destino.
  //
  // Antes de 27/08 isto apontava para `/despesas/me`, que é o ENDPOINT da API e
  // não uma tela. A URL caía no curinga `/despesas/:id`, virava `Number('me')`
  // = NaN, e o formulário reabria em modo EDIÇÃO de uma despesa inexistente —
  // o Denílson lançou a primeira despesa dele em campo e levou 403 três vezes
  // tentando salvar, porque editar exige `despesas.lancamento.editar`.
  const destinoAoSair = desktop && ehGestao ? '/despesas' : '/inicio';
  const contexto = contextoDoPerfil(usuario?.perfil);

  const [form, setForm] = useState<FormState>(FORM_VAZIO);
  const [formas, setFormas] = useState<FormaPgtoOpcao[]>([]);
  const [lojas, setLojas] = useState<Loja[]>([]);
  // Lançando a partir da página de UMA loja (`/despesas/lancar?loja=tr`), a
  // despesa já nasce nela — é o ponto de ter uma página por loja.
  const [params] = useSearchParams();
  const lojaDaPagina = (params.get('loja') ?? '').toUpperCase();
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [viagens, setViagens] = useState<ViagemOpcao[]>([]);
  const [osLista, setOsLista] = useState<OSOpcao[]>([]);

  // Metadados (só leitura) da despesa em edição.
  const [origem, setOrigem] = useState<string | null>(null);
  const [lancadoPor, setLancadoPor] = useState<string | null>(null);
  const [noTiny, setNoTiny] = useState(false);
  const [arquivadaEm, setArquivadaEm] = useState<string | null>(null);
  // SEM VIAGEM precisa ser DITO, não suposto (dele, 26/09): com "avulsa" como
  // opção vazia do select, esquecer de escolher a viagem e não ter viagem
  // davam no mesmo — e as despesas antigas ficaram soltas assim. Não vai ao
  // banco: `viagem_id` nulo já diz tudo; isto só obriga a responder.
  const [semViagem, setSemViagem] = useState(false);

  const [carregando, setCarregando] = useState(editando);
  const [salvando, setSalvando] = useState(false);
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editando || !lojaDaPagina || !lojas.length) return;
    const l = lojas.find((x) => (x.codigo ?? '').toUpperCase() === lojaDaPagina);
    if (l) setForm((f) => (f.loja_id ? f : { ...f, loja_id: String(l.id) }));
  }, [editando, lojaDaPagina, lojas]);

  function setCampo<K extends keyof FormState>(campo: K, valor: FormState[K]) {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  /** Escolher viagem zera a OS (a lista muda) e herda a loja dona da viagem. */
  function escolherViagem(vid: string) {
    if (vid) setSemViagem(false);
    setForm((f) => {
      const novo = { ...f, viagem_id: vid, os_id: '' };
      const v = viagens.find((x) => String(x.id) === vid);
      if (v && v.loja_id) novo.loja_id = String(v.loja_id);
      return novo;
    });
  }

  /** Trocar a forma limpa o "cartão de qual loja" quando a nova não pede. */
  function escolherForma(codigo: string) {
    setForm((f) => {
      const nova = formas.find((x) => x.codigo === codigo);
      return {
        ...f,
        forma_pgto: codigo,
        loja_cartao_id: nova?.pede_loja ? f.loja_cartao_id : '',
      };
    });
  }

  // Opções (uma vez).
  useEffect(() => {
    api.get<Loja[]>('/lojas').then(setLojas).catch(() => undefined);
    api
      .get<Categoria[]>('/despesas/categorias')
      .then(setCategorias)
      .catch(() => undefined);
    api
      .get<{ viagens: ViagemOpcao[] }>('/public/despesas/opcoes')
      .then((op) => setViagens(op.viagens ?? []))
      .catch(() => undefined);
  }, []);

  // Formas de pagamento: filtradas por quem está logado (celular) ou todas (desktop).
  useEffect(() => {
    api
      .get<FormaPgtoOpcao[]>(`/despesas/formas-pagamento${desktop ? '?todos=true' : ''}`)
      .then(setFormas)
      .catch(() => undefined);
  }, [desktop]);

  // OS conforme a viagem escolhida.
  useEffect(() => {
    let vivo = true;
    const qs = form.viagem_id ? `?viagem_id=${form.viagem_id}` : '';
    api
      .get<OSOpcao[]>(`/public/despesas/os${qs}`)
      .then((l) => {
        if (vivo) setOsLista(l ?? []);
      })
      .catch(() => {
        if (vivo) setOsLista([]);
      });
    return () => {
      vivo = false;
    };
  }, [form.viagem_id]);

  // Despesa nova: Búzios por padrão (ajustável), enquanto não houver loja.
  useEffect(() => {
    if (editando || !lojas.length) return;
    setForm((f) => {
      if (f.loja_id) return f;
      const bz = lojas.find((l) => l.codigo === 'BZ');
      return bz ? { ...f, loja_id: String(bz.id) } : f;
    });
  }, [lojas, editando]);

  // Carrega a despesa em edição.
  useEffect(() => {
    if (!editando || despesaId == null) return;
    setCarregando(true);
    api
      .get<DespesaSaida>(`/despesas/${despesaId}`)
      .then((d) => {
        setForm({
          data: d.data ? d.data.slice(0, 10) : hojeISO(),
          valor: doBanco(d.valor, 2, 'despesa.valor'),
          historico: d.historico ?? '',
          descricao: d.descricao ?? '',
          categoria_id: d.categoria_id ? String(d.categoria_id) : '',
          forma_pgto: d.forma_pgto ?? '',
          loja_cartao_id: d.loja_cartao_id ? String(d.loja_cartao_id) : '',
          loja_id: d.loja_id ? String(d.loja_id) : '',
          viagem_id: d.viagem_id ? String(d.viagem_id) : '',
          os_id: d.os_id ? String(d.os_id) : '',
          comprovante: d.comprovante
            ? d.comprovante.replace(/^\/uploads\//, '')
            : null,
        });
        setSemViagem(!d.viagem_id);
        setOrigem(d.origem);
        setLancadoPor(d.lancado_por ?? null);
        setNoTiny(['enviando', 'enviada', 'verificar', 'ja_no_tiny'].includes(d.envio_tiny ?? ''));
        setArquivadaEm(d.arquivada_em ?? null);
      })
      .catch((err) =>
        toast.error(
          err instanceof ApiError
            ? err.message
            : 'Não foi possível carregar a despesa.',
        ),
      )
      .finally(() => setCarregando(false));
  }, [editando, despesaId]);

  async function onArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setEnviandoFoto(true);
    try {
      // Comprime antes de subir: a foto vem de celular e vai para o PDF.
      const comprimido = await comprimirImagem(file);
      const fd = new FormData();
      fd.append('arquivo', comprimido);
      const resp = await api.upload<UploadComprovante>(
        '/despesas/comprovantes',
        fd,
      );
      setCampo('comprovante', resp.arquivo);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Falha ao enviar o comprovante.',
      );
    } finally {
      setEnviandoFoto(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function salvar(e: React.FormEvent) {
    e.preventDefault();
    // O campo entrega string canônica: `paraConta` para validar, `paraBanco`
    // para o payload — as duas bordas de lib/numero, nunca conversão na mão.
    if (paraConta(form.valor) <= 0) {
      toast.error('Informe um valor maior que zero.');
      return;
    }
    if (!form.data) {
      toast.error('Informe a data.');
      return;
    }
    if (!desktop && !form.viagem_id && !semViagem) {
      toast.error('Escolha a viagem ou marque "Sem viagem".');
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        data: form.data,
        valor: paraBanco(form.valor),
        historico: form.historico.trim() || null,
        descricao: form.descricao.trim() || null,
        categoria_id: form.categoria_id ? Number(form.categoria_id) : null,
        forma_pgto: form.forma_pgto || null,
        loja_cartao_id: form.loja_cartao_id ? Number(form.loja_cartao_id) : null,
        loja_id: form.loja_id ? Number(form.loja_id) : null,
        viagem_id: form.viagem_id ? Number(form.viagem_id) : null,
        os_id: form.os_id ? Number(form.os_id) : null,
        comprovante: form.comprovante,
      };
      // NO ESCRITÓRIO, SALVAR NÃO TIRA DA TELA (dele, 25/09): "eu salvo,
      // depois quero continuar fazendo alguma coisa" — a aba Tiny, por
      // exemplo. Editando, fica; lançando, vai para a despesa recém-criada.
      // No celular segue saindo: lá o gesto é lançar e ir embora.
      if (editando) {
        await api.put(`/despesas/${despesaId}`, payload);
        toast.success('Despesa atualizada.');
        if (desktop) return;
      } else {
        const nova = await api.post<{ id: number }>('/despesas', payload);
        toast.success('Despesa lançada.');
        if (desktop && nova?.id) {
          navigate(`/despesas/${nova.id}`, { replace: true });
          return;
        }
      }
      navigate(destinoAoSair);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Não foi possível salvar.',
      );
    } finally {
      setSalvando(false);
    }
  }

  const formaEscolhida = formas.find((f) => f.codigo === form.forma_pgto);

  return {
    form,
    setCampo,
    escolherViagem,
    semViagem,
    marcarSemViagem: (v: boolean) => {
      setSemViagem(v);
      if (v) setForm((f) => ({ ...f, viagem_id: '', os_id: '' }));
    },
    escolherForma,
    formas,
    formaEscolhida,
    pedeLoja: !!formaEscolhida?.pede_loja,
    lojas,
    categorias,
    viagens,
    osLista,
    categoriaEscolhida: categorias.find((c) => String(c.id) === form.categoria_id),
    // Nome CURTO ("Búzios"): em select e lista o "CasaA" é ruído (migration 051).
    rotuloLoja: (l: Loja) => l.nome_curto || l.nome,
    editando,
    origem,
    lancadoPor,
    noTiny,
    setNoTiny,
    arquivadaEm,
    setArquivadaEm,
    contexto,
    ehGestao,
    destinoAoSair,
    carregando,
    salvando,
    enviandoFoto,
    fileRef,
    onArquivo,
    removerComprovante: () => setCampo('comprovante', null),
    salvar,
    sair: () => navigate(destinoAoSair),
  };
}
